import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Chip, IconButton, TableCell, TableRow } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';

const appointmentShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  date: PropTypes.string,
  time: PropTypes.string,
  patientName: PropTypes.string,
  patientPhoneNumber: PropTypes.string,
  dentistName: PropTypes.string,
  appointmentType: PropTypes.string,
  roomName: PropTypes.string,
  observation: PropTypes.string,
  cancellationReason: PropTypes.string,
});

function AppointmentDetailCells({ appointment, showCancellationReason }) {
  return (
    <>
      <TableCell>{appointment.dentistName || '—'}</TableCell>
      <TableCell>{appointment.appointmentType || '—'}</TableCell>
      <TableCell>{appointment.roomName || '—'}</TableCell>
      <TableCell>{appointment.observation || '—'}</TableCell>
      {showCancellationReason && <TableCell>{appointment.cancellationReason || '—'}</TableCell>}
    </>
  );
}

AppointmentDetailCells.propTypes = {
  appointment: appointmentShape.isRequired,
  showCancellationReason: PropTypes.bool.isRequired,
};

export default function DashboardPatientAppointmentGroup({ appointments, showCancellationReason }) {
  const [isExpanded, setExpanded] = useState(false);
  const [first] = appointments;
  const detailColumnCount = showCancellationReason ? 5 : 4;

  if (appointments.length === 1) {
    return (
      <TableRow hover>
        <TableCell />
        <TableCell>{first.patientName}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{first.patientPhoneNumber || '—'}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{first.date}</TableCell>
        <TableCell>{first.time}</TableCell>
        <AppointmentDetailCells
          appointment={first}
          showCancellationReason={showCancellationReason}
        />
      </TableRow>
    );
  }

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded((expanded) => !expanded)}
        sx={{ cursor: 'pointer', '& > td': { fontWeight: 600 } }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={isExpanded ? 'Recolher' : 'Expandir'}>
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </IconButton>
        </TableCell>
        <TableCell>
          {first.patientName}
          <Chip
            label={`${appointments.length} consultas`}
            size="small"
            color="warning"
            sx={{ ml: 1, fontWeight: 600 }}
          />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{first.patientPhoneNumber || '—'}</TableCell>
        <TableCell colSpan={2 + detailColumnCount} sx={{ color: 'text.secondary' }}>
          {appointments.map((appointment) => appointment.date).join(', ')}
        </TableCell>
      </TableRow>
      {isExpanded &&
        appointments.map((appointment) => (
          <TableRow key={appointment.id} sx={{ bgcolor: 'action.hover' }}>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{appointment.date}</TableCell>
            <TableCell>{appointment.time}</TableCell>
            <AppointmentDetailCells
              appointment={appointment}
              showCancellationReason={showCancellationReason}
            />
          </TableRow>
        ))}
    </>
  );
}

DashboardPatientAppointmentGroup.propTypes = {
  appointments: PropTypes.arrayOf(appointmentShape).isRequired,
  showCancellationReason: PropTypes.bool.isRequired,
};
